<template>
    <EntityDetailsPropertiesTabCard class="EntityDetailsRiskProfilesCard" title="Risk Profiles">
        <template v-if="entityRiskProfiles.length > 0" #titleRight>
            <span class="EntityDetailsRiskProfilesCard__total">
                {{ totalRequirements }} {{ totalRequirements === 1 ? 'Requirement' : 'Requirements' }}
            </span>
        </template>

        <div class="EntityDetailsRiskProfilesCard__list">
            <div
                v-for="profile in entityRiskProfiles"
                :key="profile.id"
                class="EntityDetailsRiskProfilesCard__item"
                role="button"
                tabindex="0"
                @click="$emit('profile-click', profile)"
                @keydown.enter="$emit('profile-click', profile)"
            >
                <div
                    class="EntityDetailsRiskProfilesCard__icon"
                    :class="`EntityDetailsRiskProfilesCard__icon--${profile.complianceStatus}`"
                >
                    <EidIcon :icon-src="getRiskProfileTypeIconSrc(profile.ccgType)" />
                </div>
                <span class="EntityDetailsRiskProfilesCard__name">
                    {{ profile.displayName }}
                    <span class="EntityDetailsRiskProfilesCard__requirements">
                        – {{ profile.coverageTypes.length }} {{ profile.coverageTypes.length === 1 ? 'Requirement' : 'Requirements' }}
                    </span>
                </span>
                <ComplianceStatusBadge :status="profile.complianceStatus" />
            </div>
            <p v-if="entityRiskProfiles.length === 0" class="EntityDetailsRiskProfilesCard__empty">
                No risk profiles assigned
            </p>
        </div>
    </EntityDetailsPropertiesTabCard>
</template>

<style lang="scss">
    .EntityDetailsRiskProfilesCard {
        &__list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 16px 0;
        }

        &__item {
            display: flex;
            align-items: center;
            gap: 10px;
            background: var(--eidBgGrayTones-lightest);
            border: 1px solid rgba(0, 0, 0, 0.07);
            border-radius: 8px;
            padding: 8px 12px;
            cursor: pointer;
            transition: background 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
            outline: none;

            &:hover {
                background: var(--eidTrustGreen-10);
                border-color: var(--eidTrustGreen-25);
                box-shadow: 0 2px 8px rgba(43, 181, 151, 0.1);
            }

            &:focus-visible {
                border-color: var(--eidTrustGreen);
                box-shadow: 0 0 0 2px var(--eidTrustGreen-10);
            }

            .Badge {
                font-size: 12px;
                padding: 2px 8px;
                line-height: 16px;
                border-radius: 4px;
            }
        }

        &__icon {
            flex-shrink: 0;
            width: 28px;
            height: 28px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            background: var(--eidTrustGreen-10);
            color: var(--eidTrustGreen);

            .EidIcon {
                width: 14px;
                height: 14px;
            }

            &--NON_COMPLIANT {
                background: var(--eidDangerRed-10);
                color: var(--eidDangerRed);
            }

            &--PENDING {
                background: var(--eidImpactOrange-10);
                color: var(--eidImpactOrange);
            }

            &--NEW {
                background: var(--eidQualityBlack-10);
                color: var(--eidQualityBlack-50);
            }
        }

        &__total {
            font-family: "Roboto", sans-serif;
            font-size: 13px;
            font-weight: 400;
            color: var(--eidQualityBlack-50);
        }

        &__name {
            flex: 1;
            font-family: "Roboto", sans-serif;
            font-size: 13px;
            font-weight: 500;
            line-height: 20px;
            color: var(--eidSecureBlue);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &__requirements {
            font-weight: 400;
            color: var(--eidQualityBlack-50);
        }

        &__empty {
            font-family: "Roboto", sans-serif;
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
            color: var(--eidQualityBlack-50);
            text-align: center;
            padding: 16px 0;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { EntityRiskProfile } from '@evidentid/tprm-portal-lib/models/decisioning';

    import ComplianceStatusBadge from '@/modules/dashboard/components/ComplianceStatusBadge/ComplianceStatusBadge.vue';
    import { getRiskProfileTypeIconSrc } from '@/modules/decisioning-criteria/utils/get-risk-profile-type-icon-src/getRiskProfileTypeIconSrc';

    import EntityDetailsPropertiesTabCard from './EntityDetailsPropertiesTabCard.vue';

    export default defineComponent({
        name: 'EntityDetailsRiskProfilesCard',
        components: {
            EidIcon,
            ComplianceStatusBadge,
            EntityDetailsPropertiesTabCard,
        },
        props: {
            entityRiskProfiles: {
                type: Array as PropType<EntityRiskProfile[]>,
                default: (): EntityRiskProfile[] => [],
            },
        },
        emits: [ 'profile-click' ],
        computed: {
            totalRequirements(): number {
                return this.entityRiskProfiles.reduce((sum, p) => sum + p.coverageTypes.length, 0);
            },
        },
        methods: {
            getRiskProfileTypeIconSrc,
        },
    });
</script>
